import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          theme?: 'dark' | 'light' | 'auto';
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        },
      ) => string;
      remove: (widgetId: string) => void;
    };
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let scriptLoading: Promise<void> | null = null;

function loadScript(): Promise<void> {
  scriptLoading ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Turnstile failed to load'));
    document.head.append(script);
  });
  return scriptLoading;
}

/**
 * Cloudflare Turnstile ("I'm human" check). Renders nothing when the API has it switched off.
 * Change `resetKey` to get a fresh token after a failed submit (tokens are single-use).
 */
export function Turnstile({
  siteKey,
  onToken,
  resetKey = 0,
}: {
  siteKey: string | null;
  onToken: (token: string | null) => void;
  resetKey?: number;
}) {
  const container = useRef<HTMLDivElement>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  // biome-ignore lint/correctness/useExhaustiveDependencies: resetKey re-renders the widget on purpose.
  useEffect(() => {
    if (!siteKey || !container.current) return;
    const element = container.current;
    let widgetId: string | undefined;
    let cancelled = false;
    onTokenRef.current(null);

    loadScript()
      .then(() => {
        if (cancelled || !window.turnstile) return;
        widgetId = window.turnstile.render(element, {
          sitekey: siteKey,
          theme: 'light',
          callback: (token) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(null),
          'error-callback': () => onTokenRef.current(null),
        });
      })
      .catch(() => onTokenRef.current(null));

    return () => {
      cancelled = true;
      if (widgetId) window.turnstile?.remove(widgetId);
    };
  }, [siteKey, resetKey]);

  if (!siteKey) return null;
  return <div ref={container} className="min-h-[65px]" />;
}

/** Header better-auth's captcha plugin reads the token from. */
export function captchaHeaders(token: string | null): Record<string, string> {
  return token ? { 'x-captcha-response': token } : {};
}
